'use client';

import React from 'react';

export interface Filters {
  sector: string;
  tipo: string;
  impacto: string[];
  innovacion: string;
  modelo: string;
}

const SECTORS = [
  { value: 'Tecnología', emoji: '💻' },
  { value: 'Salud', emoji: '🏥' },
  { value: 'Educación', emoji: '📚' },
  { value: 'Finanzas', emoji: '💰' },
  { value: 'Retail / E-commerce', emoji: '🛒' },
  { value: 'Alimentación', emoji: '🍽️' },
  { value: 'Entretenimiento', emoji: '🎮' },
  { value: 'Logística', emoji: '🚚' },
  { value: 'Energía', emoji: '⚡' },
  { value: 'Turismo', emoji: '✈️' },
  { value: 'Inmobiliaria', emoji: '🏠' },
  { value: 'Otro', emoji: '✨' },
];

const TIPOS = [
  { value: 'Producto físico', emoji: '📦' },
  { value: 'Software / App', emoji: '📱' },
  { value: 'Servicio', emoji: '🤝' },
  { value: 'Plataforma', emoji: '🔗' },
  { value: 'Marketplace', emoji: '🏪' },
  { value: 'Contenido / Media', emoji: '🎬' },
];

const IMPACTOS = [
  { value: 'Sustentable / Verde', emoji: '🌱' },
  { value: 'Impacto social', emoji: '💙' },
  { value: 'Escalable globalmente', emoji: '🌍' },
  { value: 'Local / Regional', emoji: '📍' },
];

const INNOVACIONES = [
  { value: 'Disruptiva', emoji: '🚀' },
  { value: 'Incremental', emoji: '📈' },
  { value: 'Científica / DeepTech', emoji: '🔬' },
];

const MODELOS = [
  { value: 'B2B', emoji: '🏢' },
  { value: 'B2C', emoji: '👤' },
  { value: 'B2B2C', emoji: '🔄' },
  { value: 'SaaS', emoji: '☁️' },
  { value: 'D2C', emoji: '📬' },
];

interface Props {
  filters: Filters;
  onChange: (filters: Filters) => void;
}

function PillGroup<T extends string>({
  label,
  items,
  selected,
  multi,
  onToggle,
}: {
  label: string;
  items: { value: T; emoji: string }[];
  selected: T | T[];
  multi?: boolean;
  onToggle: (value: T) => void;
}) {
  const isActive = (v: T) =>
    multi ? (selected as T[]).includes(v) : selected === v;

  return (
    <div className="space-y-2">
      <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">{label}</p>
      <div className="flex flex-wrap gap-2">
        {items.map(({ value, emoji }) => (
          <button
            key={value}
            type="button"
            onClick={() => onToggle(value)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border
              ${isActive(value)
                ? 'bg-gradient-to-r from-orange-500 to-pink-500 border-transparent text-white shadow-lg shadow-pink-500/20 scale-105'
                : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/20'
              }
            `}
          >
            <span>{emoji}</span>
            <span>{value}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ filters, onChange }: Props) {
  const toggle = <K extends keyof Filters>(key: K, value: string) => {
    if (key === 'impacto') {
      const current = filters.impacto;
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      onChange({ ...filters, impacto: next });
    } else {
      onChange({ ...filters, [key]: filters[key] === value ? '' : value });
    }
  };

  return (
    <div className="space-y-5">
      <PillGroup
        label="Sector"
        items={SECTORS}
        selected={filters.sector}
        onToggle={(v) => toggle('sector', v)}
      />
      <PillGroup
        label="Tipo de negocio"
        items={TIPOS}
        selected={filters.tipo}
        onToggle={(v) => toggle('tipo', v)}
      />
      <PillGroup
        label="Impacto (múltiple)"
        items={IMPACTOS}
        selected={filters.impacto}
        multi
        onToggle={(v) => toggle('impacto', v)}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <PillGroup
          label="Innovación"
          items={INNOVACIONES}
          selected={filters.innovacion}
          onToggle={(v) => toggle('innovacion', v)}
        />
        <PillGroup
          label="Modelo de negocio"
          items={MODELOS}
          selected={filters.modelo}
          onToggle={(v) => toggle('modelo', v)}
        />
      </div>
    </div>
  );
}
